/**
 * Automated Email Triggers for Omnisend
 * Sets up email sequences for trial abandonment, nurturing, and behavioral triggers
 */

// Helper function to get user segment based on behavior
const getUserSegment = (): string => {
  const journey = JSON.parse(sessionStorage.getItem('current_journey') || '{}');
  const device = journey.device || 'desktop';
  const source = journey.source || 'direct';
  const userType = journey.userType || 'new';
  
  // Create segment based on multiple factors
  return `${userType}_${device}_${source}`.toLowerCase();
};

// Helper function to get trial status
const getTrialStatus = (): string => {
  return localStorage.getItem('trial_status') || 'not_started';
};

/**
 * Email trigger utilities for conversion optimization
 */
export const EmailTriggers = {
  // Trial abandonment sequence
  setupTrialAbandonmentSequence: (abandonmentStep: string, userEmail?: string) => {
    const userSegment = getUserSegment();
    const baseProperties = {
      user_segment: userSegment,
      abandonment_step: abandonmentStep,
      user_email: userEmail
    };

    // Trigger 1: Immediate (within 1 hour) - Gentle reminder
    if (typeof window !== 'undefined' && window.omnisend) {
      window.omnisend.push(['track', 'trigger_trial_abandonment_immediate', {
        ...baseProperties,
        trigger_delay: '1_hour',
        message_type: 'gentle_reminder',
        personalization: {
          urgency_level: 'low',
          tone: 'helpful',
          cta_text: 'Continue Your Trial Setup'
        }
      }]);
    }

    // Trigger 2: 24 hours later - Value reinforcement
    setTimeout(() => {
      if (getTrialStatus() === 'not_started') {
        if (typeof window !== 'undefined' && window.omnisend) {
          window.omnisend.push(['track', 'trigger_trial_abandonment_24h', {
            ...baseProperties,
            trigger_delay: '24_hours',
            message_type: 'value_reinforcement',
            personalization: {
              urgency_level: 'medium',
              tone: 'educational',
              cta_text: 'See What You\'re Missing'
            }
          }]);
        }
      }
    }, 1000); // Immediate trigger setup, Omnisend handles timing

    // Trigger 3: 3 days later - Special offer
    setTimeout(() => {
      if (getTrialStatus() === 'not_started') {
        if (typeof window !== 'undefined' && window.omnisend) {
          window.omnisend.push(['track', 'trigger_trial_abandonment_3d', {
            ...baseProperties,
            trigger_delay: '3_days',
            message_type: 'special_offer',
            offer_type: 'extended_trial',
            personalization: {
              urgency_level: 'high',
              tone: 'persuasive',
              cta_text: 'Get Extended Trial Access'
            }
          }]);
        }
      }
    }, 1000);
  },

  // Trial nurturing sequence for active trial users
  setupTrialNurturingSequence: (userId: string, userEmail: string, planType: string = 'trial') => {
    const baseProperties = {
      user_id: userId,
      user_email: userEmail,
      plan_type: planType,
      sequence_type: 'trial_nurturing'
    };

    // Day 1: Welcome & getting started
    if (typeof window !== 'undefined' && window.omnisend) {
      window.omnisend.push(['track', 'trigger_trial_welcome', {
        ...baseProperties,
        trigger_delay: '1_day',
        message_type: 'welcome_onboarding',
        goal: 'first_connection',
        personalization: {
          onboarding_step: 'account_setup',
          primary_feature: 'dashboard_overview'
        }
      }]);

      // Day 3: Feature education
      window.omnisend.push(['track', 'trigger_trial_education', {
        ...baseProperties,
        trigger_delay: '3_days',
        message_type: 'feature_education',
        goal: 'feature_discovery',
        personalization: {
          featured_tools: ['portfolio_tracking', 'bot_analytics', 'performance_insights']
        }
      }]);

      // Day 7: Mid-trial value demonstration
      window.omnisend.push(['track', 'trigger_trial_midpoint', {
        ...baseProperties,
        trigger_delay: '7_days',
        message_type: 'value_demonstration',
        goal: 'value_realization',
        days_remaining: 7,
        personalization: {
          usage_stats: true,
          success_stories: true,
          advanced_features: ['strategy_backtesting', 'multi_exchange_sync']
        }
      }]);

      // Day 10: Success stories and social proof
      window.omnisend.push(['track', 'trigger_trial_social_proof', {
        ...baseProperties,
        trigger_delay: '10_days',
        message_type: 'social_proof',
        goal: 'confidence_building',
        days_remaining: 4,
        personalization: {
          testimonials: true,
          user_achievements: true,
          community_stats: true
        }
      }]);

      // Day 12: Upgrade reminder with benefits
      window.omnisend.push(['track', 'trigger_trial_ending_soon', {
        ...baseProperties,
        trigger_delay: '12_days',
        message_type: 'upgrade_reminder',
        goal: 'conversion',
        days_remaining: 2,
        personalization: {
          urgency_level: 'high',
          discount_offer: true,
          feature_comparison: true,
          cta_text: 'Upgrade Now to Keep Everything'
        }
      }]);
    }
  },

  // Behavioral triggers based on user actions
  setupBehavioralTriggers: (userId: string, userEmail: string) => {
    const baseProperties = {
      user_id: userId,
      user_email: userEmail
    };

    // Trigger for users who view pricing but don't upgrade
    if (typeof window !== 'undefined' && window.omnisend) {
      window.omnisend.push(['track', 'trigger_pricing_viewed_no_action', {
        ...baseProperties,
        trigger_delay: '2_hours',
        message_type: 'pricing_follow_up',
        personalization: {
          compared_plans: true,
          value_props: true,
          limited_time_offer: false
        }
      }]);

      // Trigger for users who complete setup but aren't active
      window.omnisend.push(['track', 'trigger_setup_complete_inactive', {
        ...baseProperties,
        trigger_delay: '24_hours',
        message_type: 'activation_encouragement',
        personalization: {
          quick_wins: true,
          tutorial_links: true,
          support_offer: true
        }
      }]);

      // Trigger for users showing high engagement (upsell opportunity)
      window.omnisend.push(['track', 'trigger_high_engagement_upsell', {
        ...baseProperties,
        trigger_delay: '5_days',
        message_type: 'premium_features_showcase',
        personalization: {
          advanced_features: true,
          productivity_boost: true,
          exclusive_access: true
        }
      }]);
    }
  },

  // Checkout abandonment triggers
  setupCheckoutAbandonmentSequence: (planId: string, userEmail?: string, abandonmentStep: string = 'payment') => {
    const baseProperties = {
      plan_id: planId,
      user_email: userEmail,
      abandonment_step: abandonmentStep
    };

    if (typeof window !== 'undefined' && window.omnisend) {
      // Immediate: Cart abandonment reminder
      window.omnisend.push(['track', 'trigger_checkout_abandonment_immediate', {
        ...baseProperties,
        trigger_delay: '30_minutes',
        message_type: 'cart_reminder',
        personalization: {
          plan_benefits: true,
          urgency_level: 'low',
          cta_text: 'Complete Your Purchase'
        }
      }]);

      // 24 hours: Incentive offer
      window.omnisend.push(['track', 'trigger_checkout_abandonment_24h', {
        ...baseProperties,
        trigger_delay: '24_hours',
        message_type: 'incentive_offer',
        offer_type: 'discount',
        personalization: {
          discount_percentage: 15,
          urgency_level: 'medium',
          limited_time: true
        }
      }]);

      // 3 days: Final attempt with stronger incentive
      window.omnisend.push(['track', 'trigger_checkout_abandonment_final', {
        ...baseProperties,
        trigger_delay: '3_days',
        message_type: 'final_attempt',
        offer_type: 'extended_trial_plus_discount',
        personalization: {
          discount_percentage: 25,
          urgency_level: 'high',
          final_chance: true
        }
      }]);
    }
  },

  // Re-engagement for trial users who haven't logged in
  setupReengagementSequence: (userId: string, userEmail: string, lastLoginDays: number) => {
    if (lastLoginDays < 3) return; // Only for users inactive for 3+ days
    
    const baseProperties = {
      user_id: userId,
      user_email: userEmail,
      inactive_days: lastLoginDays
    };

    if (typeof window !== 'undefined' && window.omnisend) {
      window.omnisend.push(['track', 'trigger_reengagement_gentle', {
        ...baseProperties,
        trigger_delay: 'immediate',
        message_type: 'gentle_reengagement',
        personalization: {
          missed_updates: true,
          quick_value: true,
          easy_return: true
        }
      }]);

      // If still inactive after 7 days, stronger message
      if (lastLoginDays >= 7) {
        window.omnisend.push(['track', 'trigger_reengagement_strong', {
          ...baseProperties,
          trigger_delay: 'immediate',
          message_type: 'strong_reengagement',
          personalization: {
            trial_expiring: true,
            value_lost: true,
            special_support: true
          }
        }]);
      }
    }
  },

  // Win-back campaigns for expired trials
  setupWinBackCampaign: (userId: string, userEmail: string, expiredDays: number) => {
    const baseProperties = {
      user_id: userId,
      user_email: userEmail,
      expired_days: expiredDays
    };

    if (typeof window !== 'undefined' && window.omnisend) {
      // 1 week after expiration
      window.omnisend.push(['track', 'trigger_winback_week', {
        ...baseProperties,
        trigger_delay: '7_days',
        message_type: 'winback_offer',
        offer_type: 'new_trial',
        personalization: {
          second_chance: true,
          improved_features: true,
          exclusive_access: true
        }
      }]);

      // 1 month after expiration
      window.omnisend.push(['track', 'trigger_winback_month', {
        ...baseProperties,
        trigger_delay: '30_days',
        message_type: 'major_updates',
        personalization: {
          new_features: true,
          success_stories: true,
          competitive_advantage: true
        }
      }]);
    }
  }
};

export default EmailTriggers;